import type { QueryAccountHierarchiesResponse } from '@flexible-reports/store/api/accounts/api-types';
import { AccountTree } from '@flexible-reports/store/api/accounts/types';

const ADVERTISER_ACCOUNT = 'ADVERTISER_ACCOUNT';
const MANAGER_ACCOUNT = 'MANAGER_ACCOUNT';

/**
 * Response from hierarchies looks like this:
 * {
 *   accounts: [
 *     {
 *       accountId: '12345',
 *       accountName: 'My Account',
 *       accountType: 'ADVERTISER_ACCOUNT',
 *       pathFromRoot: [
 *         {
 *           accountId: 'manager.MA1',
 *           accountName: 'MA 1 Account',
 *           accountType: 'MANAGER_ACCOUNT'
 *         },
 *         {
 *           accountId: 'manager.MA2',
 *           accountName: 'MA 2 Account',
 *           accountType: 'MANAGER_ACCOUNT'
 *         }
 *       ]
 *     },
 *     {
 *       accountId: '444',
 *       accountName: 'My Second Account',
 *       accountType: 'ADVERTISER_ACCOUNT',
 *       pathFromRoot: [
 *         {
 *           accountId: 'manager.MA2',
 *           accountName: 'MA 2 Account',
 *           accountType: 'MANAGER_ACCOUNT'
 *         }
 *       ]
 *     },
 *     {
 *       accountId: '555',
 *       accountName: 'My Third Account',
 *       accountType: 'ADVERTISER_ACCOUNT',
 *       pathFromRoot: []
 *     }
 *   ]
 * }
 * Essentially it's a BFS structure that we're reorganizing so that we can do level order expansion. To do that, we return
 * a table that maps from an id to an AccountTree object. The AccountTree object contains the account information,
 * a list of children ids and all descendent advertiser ids. We also include rootAccounts which represents the first level of the hierarchy.
 */
export const transformFromQueryAccountHierarchies = (response: QueryAccountHierarchiesResponse) => {
  const accountsTreeById: Record<string, AccountTree> = {};
  const rootAccounts: Set<AccountTree> = new Set();
  const advertiserAccountIds: Set<string> = new Set();
  const managerAccountIds: Set<string> = new Set();

  // Build tree structure
  for (const account of response.accounts ?? []) {
    if (!accountsTreeById[account.accountId]) {
      accountsTreeById[account.accountId] = {
        accountId: account.accountId,
        accountName: account.accountName,
        accountType: account.accountType,
        links: [],
        descendantAdvertisers: [],
        descendantManagers: [],
        immediateParents: []
      };

      // Collect account IDs by type
      if (account.accountType === ADVERTISER_ACCOUNT) {
        advertiserAccountIds.add(account.accountId);
      } else if (account.accountType === MANAGER_ACCOUNT) {
        managerAccountIds.add(account.accountId);
      }
    }

    const pathFromRoot = account.pathFromRoot ?? [];
    for (let i = 0; i < pathFromRoot.length; i++) {
      const parentAccount = pathFromRoot[i];
      if (!accountsTreeById[parentAccount.accountId]) {
        accountsTreeById[parentAccount.accountId] = {
          accountId: parentAccount.accountId,
          accountName: parentAccount.accountName,
          accountType: MANAGER_ACCOUNT,
          links: [],
          descendantAdvertisers: [],
          descendantManagers: [],
          immediateParents: []
        };
        managerAccountIds.add(parentAccount.accountId);
      }

      // RACS will not return paths for every node in tree, instead only for true leaf nodes. First account in such path is always root.
      if (i === 0) {
        rootAccounts.add(accountsTreeById[parentAccount.accountId]);
      }

      // Add advertiser to manager's descendant list
      if (
        account.accountType === ADVERTISER_ACCOUNT &&
        !accountsTreeById[parentAccount.accountId].descendantAdvertisers.includes(account.accountId)
      ) {
        accountsTreeById[parentAccount.accountId].descendantAdvertisers.push(account.accountId);
      }

      // Add manager to manager's descendant list
      if (
        account.accountType === MANAGER_ACCOUNT &&
        !accountsTreeById[parentAccount.accountId].descendantManagers.includes(account.accountId)
      ) {
        accountsTreeById[parentAccount.accountId].descendantManagers.push(account.accountId);
      }

      const childAccount = i + 1 < pathFromRoot.length ? pathFromRoot[i + 1].accountId : account.accountId;
      if (!accountsTreeById[parentAccount.accountId].links.includes(childAccount)) {
        accountsTreeById[parentAccount.accountId].links.push(childAccount);
      }
    }

    if (pathFromRoot.length === 0) {
      rootAccounts.add(accountsTreeById[account.accountId]);
    }
  }

  // Add immediate parents after all accounts are created
  if (response.accounts) {
    for (const account of response.accounts) {
      const { pathFromRoot } = account;

      if (pathFromRoot) {
        for (let i = 0; i < pathFromRoot.length; i++) {
          const parentAccount = pathFromRoot[i];
          const childAccount = i + 1 < pathFromRoot.length ? pathFromRoot[i + 1].accountId : account.accountId;

          const childAccountObj = accountsTreeById[childAccount];
          if (childAccountObj && !childAccountObj.immediateParents.some(p => p.accountId === parentAccount.accountId)) {
            childAccountObj.immediateParents.push({
              accountId: parentAccount.accountId,
              accountName: parentAccount.accountName
            });
          }
        }
      }
    }
  }

  return {
    accountsTreeById,
    rootAccounts: Array.from(rootAccounts),
    advertiserAccountIds: Array.from(advertiserAccountIds),
    managerAccountIds: Array.from(managerAccountIds),
    isMTAFeatureEnabled: response.featureEligibility?.isMTAFeatureEnabled
  };
};
